import { Heart, Pin } from 'lucide-react'

import { cn } from '@modules/lib/lib'

import type { HistoryFilter, HistoryFilterCounts } from '../hooks/useHistoryFilter'

interface HistoryFilterChipsProps {
  value: HistoryFilter
  onChange: (value: HistoryFilter) => void
  counts: HistoryFilterCounts
  /** 指定すると「ピン留め」チップに「n/上限」を出す（企業画面）。施主画面は件数を出さない */
  pinLimit?: number
  /** 「すべて」チップに件数を出すか（企業画面） */
  showAllCount?: boolean
  className?: string
}

/**
 * 履歴一覧の絞り込みチップ（「すべて」「ピン留め」「お気に入り」。決定事項2）。
 * 企業・施主で共通。見た目の差（件数の有無）はpropsで切り替える。
 */
export const HistoryFilterChips: React.FC<HistoryFilterChipsProps> = ({
  value,
  onChange,
  counts,
  pinLimit,
  showAllCount = false,
  className,
}) => {
  const chips: { key: HistoryFilter; label: string; icon?: React.ReactNode; count?: string }[] = [
    { key: 'all', label: 'すべて', count: showAllCount ? String(counts.all) : undefined },
    {
      key: 'pinned',
      label: 'ピン留め',
      icon: <Pin aria-hidden className="h-3 w-3" />,
      count: pinLimit ? `${counts.pinned}/${pinLimit}` : undefined,
    },
    {
      key: 'favorited',
      label: 'お気に入り',
      icon: <Heart aria-hidden className="h-3 w-3" />,
      count: counts.favorited > 0 ? String(counts.favorited) : undefined,
    },
  ]

  return (
    <div
      aria-label="履歴の絞り込み"
      className={cn('flex flex-wrap gap-1.5', className)}
      role="group"
    >
      {chips.map(chip => {
        const active = chip.key === value
        return (
          <button
            aria-pressed={active}
            className={cn(
              'inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs transition-colors',
              active
                ? 'border-foreground bg-foreground text-background'
                : 'border-input bg-background text-foreground hover:bg-muted',
            )}
            key={chip.key}
            onClick={() => onChange(chip.key)}
            type="button"
          >
            {chip.icon}
            {chip.label}
            {chip.count ? (
              <span className={cn('text-[11px]', active ? 'opacity-80' : 'text-muted-foreground')}>
                {chip.count}
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}
